import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FiEdit2, FiEye, FiPlus, FiSearch, FiTrash2, FiX } from 'react-icons/fi';
import Layout from '../components/Layout';
import Pagination from '../components/Pagination';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import SimonBundleModal from '../components/SimonBundleModal';

const SimonBundlePage = () => {
    const [bundles, setBundles] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalBundles, setTotalBundles] = useState(0);
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedBundle, setSelectedBundle] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const fetchProducts = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/simonproducts?page=1&limit=500`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'ngrok-skip-browser-warning': 'true'
                }
            });
            if (response.data.success) {
                setProducts(response.data.data || []);
            }
        } catch (err) {
            // Non-blocking
        }
    };

    const fetchBundles = async (page = 1, search = '') => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            let apiUrl = `${process.env.REACT_APP_API_URL}/api/simonbundles?page=${page}&limit=20`;
            if (search) apiUrl += `&search=${encodeURIComponent(search)}`;

            const response = await axios.get(apiUrl, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'ngrok-skip-browser-warning': 'true'
                }
            });
            if (response.data.success) {
                setBundles(response.data.data);
                setTotalBundles(response.data.pagination.total);
                setTotalPages(response.data.pagination.pages);
            }
        } catch (err) {
            setError(`Failed to fetch Simon bundles: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProducts();
    }, []);

    useEffect(() => {
        fetchBundles(currentPage, searchTerm);
    }, [currentPage, searchTerm]);

    const handleSearch = () => {
        setSearchTerm(searchInput);
        setCurrentPage(1);
    };

    const handleClear = () => {
        setSearchInput('');
        setSearchTerm('');
        setCurrentPage(1);
    };

    const handleDelete = async () => {
        if (!selectedBundle) return;
        try {
            setDeleteLoading(true);
            const token = localStorage.getItem('token');
            await axios.delete(`${process.env.REACT_APP_API_URL}/api/simonbundles/${selectedBundle._id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setShowDeleteModal(false);
            setSelectedBundle(null);
            fetchBundles(currentPage, searchTerm);
        } catch (err) {
            setError(`Failed to delete Simon bundle: ${err.response?.data?.error || err.message}`);
        } finally {
            setDeleteLoading(false);
        }
    };

    return (
        <Layout>
            <div className="px-2 w-full max-w-none p-6">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-2xl font-bold">Simon Bundles</h1>
                        <p className="text-gray-600">Total: {totalBundles} bundles</p>
                    </div>
                    <button onClick={() => setShowCreateModal(true)} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center">
                        <FiPlus className="mr-2" />
                        Create New Bundle
                    </button>
                </div>

                <div className="mb-6">
                    <div className="flex gap-2">
                        <div className="relative flex-1">
                            <input
                                type="text"
                                className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-blue-700 font-bold"
                                placeholder="Search by bundle name..."
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                            />
                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                <FiSearch className="h-5 w-5 text-gray-400" />
                            </div>
                        </div>
                        <button onClick={handleSearch} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"><FiSearch className="mr-2" />Search</button>
                        <button onClick={handleClear} className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700">Clear</button>
                    </div>
                </div>

                {error && <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">{error}</div>}

                {showCreateModal && (
                    <SimonBundleModal
                        mode="create"
                        products={products}
                        onSuccess={() => {
                            setShowCreateModal(false);
                            fetchBundles(currentPage, searchTerm);
                        }}
                        onCancel={() => setShowCreateModal(false)}
                    />
                )}

                {showEditModal && selectedBundle && (
                    <SimonBundleModal
                        mode="edit"
                        bundle={selectedBundle}
                        products={products}
                        onSuccess={() => {
                            setShowEditModal(false);
                            setSelectedBundle(null);
                            fetchBundles(currentPage, searchTerm);
                        }}
                        onCancel={() => {
                            setShowEditModal(false);
                            setSelectedBundle(null);
                        }}
                    />
                )}

                {showDeleteModal && selectedBundle && (
                    <DeleteConfirmationModal
                        title="Delete Simon Bundle"
                        message="Are you sure you want to delete this bundle?"
                        itemName={selectedBundle.bundleName}
                        isLoading={deleteLoading}
                        onConfirm={handleDelete}
                        onCancel={() => {
                            setShowDeleteModal(false);
                            setSelectedBundle(null);
                        }}
                    />
                )}

                {showViewModal && selectedBundle && (
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                        <div className="relative top-20 mx-auto p-5 border max-w-4xl w-full shadow-lg rounded-md bg-white">
                            <div className="flex justify-between items-center border-b pb-4 mb-4">
                                <h3 className="text-xl font-medium">
                                    Simon Bundle Details - <span className="text-blue-700 font-bold">{selectedBundle.bundleName}</span>
                                </h3>
                                <button onClick={() => { setShowViewModal(false); setSelectedBundle(null); }} className="text-gray-500 hover:text-gray-700">
                                    <FiX className="w-6 h-6" />
                                </button>
                            </div>
                            <div className="space-y-6">
                                <section className="bg-gray-50 p-4 rounded-lg">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                        <div><span className="font-semibold text-gray-700">Bundle Price:</span> <span className="text-blue-700">${Number(selectedBundle.bundlePrice || 0).toFixed(2)}</span></div>
                                        <div><span className="font-semibold text-gray-700">Active:</span> <span className="text-blue-700">{selectedBundle.isActive ? 'Yes' : 'No'}</span></div>
                                        <div><span className="font-semibold text-gray-700">Order Count:</span> <span className="text-blue-700">{selectedBundle.orderCount || 0}</span></div>
                                        <div><span className="font-semibold text-gray-700">Total Revenue:</span> <span className="text-blue-700">${Number(selectedBundle.totalRevenue || 0).toFixed(2)}</span></div>
                                    </div>
                                </section>
                                <section className="bg-gray-50 p-4 rounded-lg">
                                    <h4 className="text-lg font-medium mb-3">Bundle Products</h4>
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                                            <thead className="bg-gray-100">
                                                <tr>
                                                    <th className="px-3 py-2 text-left">Category</th>
                                                    <th className="px-3 py-2 text-left">Product Name</th>
                                                    <th className="px-3 py-2 text-right">Quantity</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {(selectedBundle.products || []).map((item, index) => (
                                                    <tr key={`${selectedBundle._id}-${index}`}>
                                                        <td className="px-3 py-2">{item.product?.category || 'N/A'}</td>
                                                        <td className="px-3 py-2">{item.product?.productName || 'N/A'}</td>
                                                        <td className="px-3 py-2 text-right">{item.quantity || 0}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </section>
                            </div>
                        </div>
                    </div>
                )}

                <div className="bg-white rounded-lg shadow overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bundle Name</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bundle Price</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Products</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order Count</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Revenue</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr><td colSpan="6" className="px-6 py-4 text-center">Loading...</td></tr>
                            ) : bundles.length === 0 ? (
                                <tr><td colSpan="6" className="px-6 py-4 text-center">No Simon bundles found</td></tr>
                            ) : (
                                bundles.map((bundle) => (
                                    <tr key={bundle._id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{bundle.bundleName}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">${Number(bundle.bundlePrice || 0).toFixed(2)}</td>
                                        <td className="px-6 py-4 text-sm">
                                            {(bundle.products || []).map((item) => `${item.product?.productName || 'N/A'} x${item.quantity || 0}`).join(', ') || 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">{bundle.orderCount || 0}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">${Number(bundle.totalRevenue || 0).toFixed(2)}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                                            <button onClick={() => { setSelectedBundle(bundle); setShowViewModal(true); }} className="p-1.5 text-blue-600 hover:text-blue-900 hover:bg-blue-100 rounded-full inline-flex items-center" title="View Details"><FiEye className="w-4 h-4" /></button>
                                            <button onClick={() => { setSelectedBundle(bundle); setShowEditModal(true); }} className="p-1.5 text-green-600 hover:text-green-900 hover:bg-green-100 rounded-full inline-flex items-center" title="Edit Bundle"><FiEdit2 className="w-4 h-4" /></button>
                                            <button onClick={() => { setSelectedBundle(bundle); setShowDeleteModal(true); }} className="p-1.5 text-red-600 hover:text-red-900 hover:bg-red-100 rounded-full inline-flex items-center" title="Delete Bundle"><FiTrash2 className="w-4 h-4" /></button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
            </div>
        </Layout>
    );
};

export default SimonBundlePage;
